import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import {
  Wrench, Car, Package, Search, Pencil, Trash2, AlertTriangle, RefreshCw,
  CalendarDays, User as UserIcon, ClipboardList, Hammer, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { fmtRoDate } from "../lib/dateFmt";

const KIND_META = {
  equipment_repair:   { label: "Reparație echipament", icon: Wrench,  color: "#0ea5e9", bg: "#e0f2fe" },
  car_external:       { label: "Service extern auto", icon: Car,     color: "#7c3aed", bg: "#ede9fe" },
  car_internal_repair:{ label: "Reparație auto (intern)", icon: Hammer, color: "#ea580c", bg: "#ffedd5" },
  car_internal_service:{label: "Revizie auto (intern)",  icon: RefreshCw, color: "#16a34a", bg: "#dcfce7" },
};

export default function IstoricReparatii() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("all"); // all|equipment|car
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (kindFilter !== "all") params.kind = kindFilter;
      if (q) params.search = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/repair-history", { params });
      setData(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare istoric");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kindFilter]);

  // Debounce search + dates
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, dateFrom, dateTo]);

  const stats = useMemo(() => {
    const out = { total: data.length, equipment: 0, car: 0, cost: 0 };
    for (const e of data) {
      if (e.target_type === "equipment") out.equipment += 1;
      else if (e.target_type === "car") out.car += 1;
      if (typeof e.cost === "number") out.cost += e.cost;
    }
    return out;
  }, [data]);

  const onDelete = async (entry) => {
    if (!entry?.delete_endpoint) return;
    try {
      await api.delete(entry.delete_endpoint.replace(/^\/api/, ""));
      toast.success("Reparația a fost ștearsă");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    }
  };

  return (
    <div className="space-y-6" data-testid="istoric-reparatii-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <Wrench size={28} className="text-orange-600" />
            Istoric autoturisme &amp; echipamente reparate
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Toate reparațiile, reviziile și schimburile de piese — echipamente și autoturisme la un loc.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-btn" className="gap-2">
          <RefreshCw size={14} /> Reîmprospătează
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total intrări" value={stats.total} color="#0f172a" icon={ClipboardList} />
        <StatCard label="Echipamente" value={stats.equipment} color="#0ea5e9" icon={Package} />
        <StatCard label="Autoturisme" value={stats.car} color="#7c3aed" icon={Car} />
        <StatCard label="Cost total" value={stats.cost > 0 ? `${stats.cost.toFixed(2)} CHF` : "—"} color="#16a34a" icon={FileText} />
      </div>

      {/* Filtre */}
      <div className="card-flat p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="search-input"
            placeholder="Caută după nume, serie, număr înmatriculare, mecanic, defect..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <div>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger data-testid="kind-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate categoriile</SelectItem>
              <SelectItem value="equipment">Doar echipamente</SelectItem>
              <SelectItem value="car">Doar autoturisme</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="date-from"
            placeholder="De la"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="date-to"
            placeholder="Până la"
          />
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-center py-12 text-zinc-400">Se încarcă...</div>
      ) : data.length === 0 ? (
        <div className="card-flat p-12 text-center text-zinc-500">
          <Wrench size={36} className="mx-auto text-zinc-300 mb-3" />
          <div className="font-semibold">Nicio reparație înregistrată</div>
          <div className="text-sm mt-1">Reparațiile efectuate la echipamente și autoturisme vor apărea aici.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {data.map((entry) => (
            <RepairCard
              key={entry.id}
              entry={entry}
              onEdit={() => setEditing(entry)}
              onDelete={() => setDeleting(entry)}
            />
          ))}
        </div>
      )}

      {editing && (
        <EditDialog
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {deleting && (
        <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
          <DialogContent data-testid="delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle size={18}/> Confirmare ștergere
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm space-y-2">
              <p>Sigur ștergi această reparație? Acțiunea nu poate fi anulată.</p>
              <div className="p-3 rounded bg-zinc-50 border text-xs space-y-1">
                <div><strong>Țintă:</strong> {deleting.target_name}
                  {deleting.target_serial && <span className="text-zinc-500"> · seria {deleting.target_serial}</span>}
                  {deleting.target_plate && <span className="text-zinc-500"> · {deleting.target_plate}</span>}
                </div>
                <div><strong>Descriere:</strong> {deleting.description}</div>
                <div><strong>Data:</strong> {fmtRoDate(deleting.date) || deleting.date}</div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleting(null)} data-testid="delete-cancel">Anulează</Button>
              <Button
                onClick={() => onDelete(deleting)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-confirm"
              >
                <Trash2 size={14} className="mr-1"/> Șterge
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="card-flat p-4" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{label}</div>
          <div className="font-display text-2xl font-extrabold mt-1" style={{ color }}>{value}</div>
        </div>
        <Icon size={28} style={{ color, opacity: 0.6 }} />
      </div>
    </div>
  );
}

function RepairCard({ entry, onEdit, onDelete }) {
  const meta = KIND_META[entry.kind] || KIND_META.equipment_repair;
  const Icon = meta.icon;
  return (
    <div
      className="card-flat p-4 hover:shadow-md transition-shadow"
      style={{ borderLeft: `4px solid ${meta.color}` }}
      data-testid={`repair-card-${entry.id}`}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div
          className="w-11 h-11 rounded-md grid place-items-center flex-shrink-0"
          style={{ background: meta.bg, color: meta.color }}
        >
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              className="text-[10px] font-bold uppercase tracking-wider"
              style={{ borderColor: meta.color, color: meta.color, background: meta.bg }}
            >
              {meta.label}
            </Badge>
            <span className="text-xs text-zinc-500 inline-flex items-center gap-1">
              <CalendarDays size={12} />
              {fmtRoDate(entry.date) || entry.date || "—"}
            </span>
          </div>
          {/* Target name */}
          <div className="mt-1.5 font-display font-bold text-base flex items-center gap-2 flex-wrap">
            {entry.target_type === "equipment" ? <Package size={16} className="text-zinc-400"/> : <Car size={16} className="text-zinc-400"/>}
            <span>{entry.target_name}</span>
            {entry.target_serial && (
              <Badge variant="outline" className="text-[10px] font-mono" data-testid={`serial-${entry.id}`}>
                seria: {entry.target_serial}
              </Badge>
            )}
            {entry.target_plate && (
              <Badge variant="outline" className="text-[10px] font-mono bg-amber-50 border-amber-300 text-amber-900" data-testid={`plate-${entry.id}`}>
                {entry.target_plate}
              </Badge>
            )}
          </div>
          {/* Description */}
          <div className="mt-1.5 text-sm text-zinc-700 leading-snug">{entry.description}</div>
          {/* Parts */}
          {entry.parts_used && (
            <div className="mt-1 text-[12px] text-zinc-600">
              <strong className="text-zinc-700">🔩 Piese / consumabile:</strong> {entry.parts_used}
            </div>
          )}
          {/* Meta row */}
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 text-[12px] text-zinc-600">
            {entry.performed_by && (
              <div className="inline-flex items-center gap-1.5">
                <Hammer size={12} className="text-orange-500"/>
                <strong className="text-zinc-700">Efectuat de:</strong> {entry.performed_by}
              </div>
            )}
            {entry.reported_by && (
              <div className="inline-flex items-center gap-1.5">
                <AlertTriangle size={12} className="text-red-500"/>
                <strong className="text-zinc-700">Defect raportat de:</strong> {entry.reported_by}
                {entry.reported_reason && <span className="text-zinc-500">— &bdquo;{entry.reported_reason}&rdquo;</span>}
              </div>
            )}
            {typeof entry.duration_min === "number" && entry.duration_min > 0 && (
              <div className="inline-flex items-center gap-1.5">
                <strong className="text-zinc-700">⏱ Durată:</strong> {entry.duration_min} min
              </div>
            )}
            {typeof entry.cost === "number" && entry.cost > 0 && (
              <div className="inline-flex items-center gap-1.5">
                <strong className="text-zinc-700">💰 Cost:</strong> {entry.cost.toFixed(2)} {entry.currency || "CHF"}
              </div>
            )}
          </div>
        </div>
        {/* Actions */}
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          {entry.editable && (
            <Button
              size="sm"
              variant="outline"
              onClick={onEdit}
              data-testid={`edit-${entry.id}`}
              className="gap-1"
            >
              <Pencil size={13}/> Editează
            </Button>
          )}
          {entry.deletable && (
            <Button
              size="sm"
              variant="outline"
              onClick={onDelete}
              data-testid={`delete-${entry.id}`}
              className="gap-1 text-red-600 hover:bg-red-50 border-red-200"
            >
              <Trash2 size={13}/> Șterge
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function EditDialog({ entry, onClose, onSaved }) {
  const isEquipment = entry.kind === "equipment_repair";
  const isCarExternal = entry.kind === "car_external";

  const [form, setForm] = useState(() => {
    if (isEquipment) {
      return {
        date: entry.date || "",
        description: entry.description || "",
        performed_by: entry.performed_by || "",
        parts_used: entry.parts_used || "",
        duration_min: entry.duration_min ?? "",
        cost_chf: entry.cost ?? "",
      };
    }
    if (isCarExternal) {
      return {
        date: entry.date || "",
        service_name: entry.service_name || "",
        description: entry.description?.replace(/^.*? — /, "") || "",
        cost: entry.cost ?? "",
        currency: entry.currency || "CHF",
      };
    }
    return {};
  });

  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = isEquipment ? {
        date: form.date,
        description: form.description,
        performed_by: form.performed_by,
        parts_used: form.parts_used,
        duration_min: form.duration_min === "" ? null : Number(form.duration_min),
        cost_chf: form.cost_chf === "" ? null : Number(form.cost_chf),
      } : {
        date: form.date,
        service_name: form.service_name,
        description: form.description,
        cost: form.cost === "" ? null : Number(form.cost),
        currency: form.currency,
      };
      await api.put(entry.edit_endpoint.replace(/^\/api/, ""), payload);
      toast.success("Reparație actualizată");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil size={16} />
            Editează reparația
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-zinc-500 -mt-2">
          {entry.target_name}
          {entry.target_serial && <span> · seria {entry.target_serial}</span>}
          {entry.target_plate && <span> · {entry.target_plate}</span>}
        </div>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Data</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="edit-date"
            />
          </div>
          {isEquipment && (
            <>
              <div>
                <Label className="text-xs">Descriere reparație *</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  data-testid="edit-description"
                />
              </div>
              <div>
                <Label className="text-xs">Efectuat de</Label>
                <Input
                  value={form.performed_by}
                  onChange={(e) => setForm({ ...form, performed_by: e.target.value })}
                  data-testid="edit-performed-by"
                />
              </div>
              <div>
                <Label className="text-xs">Piese folosite</Label>
                <Input
                  value={form.parts_used}
                  onChange={(e) => setForm({ ...form, parts_used: e.target.value })}
                  data-testid="edit-parts"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Durată (minute)</Label>
                  <Input
                    type="number"
                    value={form.duration_min}
                    onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                    data-testid="edit-duration"
                  />
                </div>
                <div>
                  <Label className="text-xs">Cost (CHF)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.cost_chf}
                    onChange={(e) => setForm({ ...form, cost_chf: e.target.value })}
                    data-testid="edit-cost"
                  />
                </div>
              </div>
            </>
          )}
          {isCarExternal && (
            <>
              <div>
                <Label className="text-xs">Service / Atelier *</Label>
                <Input
                  value={form.service_name}
                  onChange={(e) => setForm({ ...form, service_name: e.target.value })}
                  data-testid="edit-service-name"
                />
              </div>
              <div>
                <Label className="text-xs">Descriere (ce s-a făcut)</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  data-testid="edit-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Cost</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.cost}
                    onChange={(e) => setForm({ ...form, cost: e.target.value })}
                    data-testid="edit-cost"
                  />
                </div>
                <div>
                  <Label className="text-xs">Moneda</Label>
                  <Input
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    data-testid="edit-currency"
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-cancel">Anulează</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save">
            {saving ? "Salvare..." : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
